"use client";

import { App, Button, Drawer, Form, Input } from "antd";
import { useEffect } from "react";
import { useRouter } from "next/navigation";
import { useCatalog } from "@/catalog/CatalogContext";
import type { Vendor } from "@/data/catalog";

interface VendorDrawerProps {
  open: boolean;
  vendor?: Vendor;
  onClose: () => void;
}

export function VendorDrawer({ open, vendor, onClose }: VendorDrawerProps) {
  const { createVendor, updateVendorName } = useCatalog();
  const { message } = App.useApp();
  const router = useRouter();
  const [form] = Form.useForm<{ name: string }>();
  const editing = vendor != null;

  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({ name: vendor?.name ?? "" });
  }, [form, open, vendor]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  const save = async () => {
    const values = await form.validateFields();
    if (editing && vendor) {
      const result = updateVendorName(vendor.id, values.name);
      if (!result.ok) {
        form.setFields([{ name: "name", errors: [result.message] }]);
        return;
      }
      message.success("Vendor name updated");
      close();
      return;
    }

    const result = createVendor(values.name);
    if (!result.ok) {
      form.setFields([{ name: "name", errors: [result.message] }]);
      return;
    }
    message.success("Vendor created");
    close();
    router.push(`/vendors/${result.vendor.id}`);
  };

  return (
    <Drawer
      title={editing ? "Edit vendor" : "New vendor"}
      open={open}
      onClose={close}
      destroyOnHidden
      size={440}
      footer={
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <Button onClick={close}>Cancel</Button>
          <Button type="primary" onClick={() => void save()}>
            {editing ? "Save" : "Create vendor"}
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" requiredMark="optional">
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, whitespace: true, message: "Enter a vendor name." }]}
        >
          <Input maxLength={200} showCount placeholder="Lotame" autoFocus />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
